import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, TemplateRef, Input } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, of } from 'rxjs';
import { catchError, filter, finalize, map, takeUntil } from 'rxjs/operators';

import { List, ListProperty, ListAttachment } from '@site-mate/dashpivot-shared-library';

import { WINDOW } from 'app/shared/factory/window.factory';
import { ListFieldBaseComponent } from 'app/shared/model/lists/list-field-base-component.model';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { FileUploaderModalService } from 'app/shared/service/file-uploader-modal.service';
import { ResourcesService } from 'app/shared/service/resources.service';
import { environment } from 'environments/environment';

import { ListPropertyService } from '../list-property.service';

interface ListAttachmentProperty extends ListProperty {
  value?: ListAttachment[];
}

@Component({
  templateUrl: './list-field-attachment.component.html',
  styleUrls: ['./list-field-attachment.component.scss'],
  selector: 'cc-list-field-attachment',
})
export class ListFieldAttachmentComponent extends ListFieldBaseComponent implements OnDestroy, OnInit {
  @Input() property: ListAttachmentProperty;

  private destroy$ = new Subject<void>();
  readonly state = {
    selectedAttachment: null as ListAttachment,
    attachments: [] as ListAttachment[],
    downloading: false,
  };
  uploadUrl = '';

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly confirmService: ConfirmService,
    private readonly errorHandler: ErrorHandler,
    private readonly ngbModal: NgbModal,
    private readonly fileUploaderModalService: FileUploaderModalService,
    private readonly resourcesService: ResourcesService,
    private readonly listPropertyService: ListPropertyService,
    @Inject(WINDOW) private readonly window: Window,
  ) {
    super();
  }

  ngOnInit() {
    this.fileUploaderModalService.allUploadsFinished$
      .pipe(
        takeUntil(this.destroy$),
        filter((data) => data.url === this.uploadUrl),
        map((data) => {
          const list = data.data as List;
          const allProperties = list.items
            .filter((item) => item.id === this.item.id)
            .reduce((carry, item) => carry.concat(item.properties), [] as ListProperty[]);
          return allProperties.find((property) => property.id === this.property.id);
        }),
        filter((property) => !!property),
      )
      .subscribe((result) => {
        this.property.value = result.value as ListAttachment[];
        this.setAttachments();
      });

    this.setAttachments();
    const uploadUrlBase = this.companyId
      ? `${environment.apiUrl}companies/${this.companyId}`
      : `${environment.apiUrl}projects/${this.projectId}`;
    this.uploadUrl = this.listPropertyService.getAddAttachmentToCellUrl(
      uploadUrlBase,
      this.listId,
      this.item.id,
      this.property.id,
    );
  }

  onPropertyUpdated({ value }: Record<string, ListAttachment[]>) {
    this.property.value = value;
    this.propertyUpdated.emit({ value });
  }

  getPropertyValue() {
    return (this.property.value ?? []) as ListAttachment[];
  }

  setAttachments(value = this.getPropertyValue()) {
    this.state.attachments = value.slice();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addAttachment() {
    this.fileUploaderModalService.add({
      url: this.uploadUrl,
      acceptedFiles: 'application/pdf',
      maxFileSize: 10,
    });
  }

  viewAttachment(templateRef: TemplateRef<any>, attachment: ListAttachment) {
    this.state.selectedAttachment = attachment;
    this.state.attachments = this.property.value as ListAttachment[];
    this.ngbModal.open(templateRef, {
      windowClass: 'attachment-modal',
    });
  }

  downloadAttachment(attachment: ListAttachment) {
    this.state.downloading = true;
    this.resourcesService
      .getDownloadUrl(attachment.fullUrl, { keyName: attachment.filename })
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          this.errorHandler.handle(error);
          return of(null);
        }),
        finalize(() => {
          this.state.downloading = false;
        }),
      )
      .subscribe((downloadUrl) => {
        this.window.location.href = downloadUrl;
      });
  }

  deleteAttachment(attachment: ListAttachment) {
    this.confirmService
      .confirmDelete('deleteListItemPropertyAttachment')
      // eslint-disable-next-line promise/always-return
      .then(() => {
        const values = this.state.attachments.slice();
        const updatedValue = values.filter((entry) => entry.fullUrl !== attachment.fullUrl);
        this.property.value = updatedValue;
        this.setAttachments();
        this.onPropertyUpdated({ value: updatedValue });
      })
      .catch(() => {});
  }
}
